import React from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Container,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Slide,
  useScrollTrigger,
  Badge,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";

interface NavbarProps {
  specialistName: string;
  specialistTitle: string;
  avatarSrc?: string;
}

interface HideOnScrollProps {
  children: React.ReactElement;
}

function HideOnScroll(props: HideOnScrollProps) {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

export const Navbar: React.FC<NavbarProps> = ({
  specialistName,
  specialistTitle,
  avatarSrc,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const navItems = ["Benefícios", "Planos", "Depoimentos", "FAQ"];

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  return (
    <HideOnScroll>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(8px)",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            disableGutters
            sx={{
              justifyContent: "space-between",
              height: 70,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <HealthAndSafetyIcon
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: 36,
                  mr: 1,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Vida Segura
              </Typography>
            </Box>

            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mx: 2,
                  "& > a": {
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: 0,
                      left: "50%",
                      width: 0,
                      height: 2,
                      backgroundColor: theme.palette.primary.main,
                      transition: "width 0.3s ease, left 0.3s ease",
                    },
                    "&:hover::after": {
                      width: "80%",
                      left: "10%",
                    },
                  },
                }}
              >
                {navItems.map((item) => (
                  <Button
                    key={item}
                    sx={{
                      mx: 1.5,
                      color: theme.palette.text.primary,
                      fontSize: "0.95rem",
                      fontWeight: 500,
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: theme.palette.primary.main,
                      },
                    }}
                    href={`#${item.toLowerCase()}`}
                  >
                    {item}
                  </Button>
                ))}
              </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center" }}>
              {!isMobile && (
                <>
                  <Badge
                    color="secondary"
                    variant="dot"
                    overlap="circular"
                    sx={{ mr: 2 }}
                  >
                    <Avatar
                      alt={specialistName}
                      src={avatarSrc}
                      sx={{
                        width: 40,
                        height: 40,
                        border: `2px solid ${theme.palette.primary.light}`,
                      }}
                    />
                  </Badge>
                  <Box sx={{ mr: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {specialistName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {specialistTitle}
                    </Typography>
                  </Box>
                </>
              )}

              <Button
                variant="contained"
                color="primary"
                href="#formulario"
                startIcon={<LocalPhoneIcon />}
                sx={{
                  display: { xs: "none", sm: "flex" },
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                }}
              >
                Falar agora
              </Button>

              {isMobile && (
                <IconButton
                  color="primary"
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerToggle}
                  sx={{ ml: 1 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>

        {/* Menu Mobile */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          PaperProps={{
            sx: {
              width: "85%",
              maxWidth: "350px",
              borderTopLeftRadius: 16,
              borderBottomLeftRadius: 16,
              p: 3,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <HealthAndSafetyIcon
                sx={{ color: theme.palette.primary.main, fontSize: 28, mr: 1 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Vida Segura
              </Typography>
            </Box>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{ color: theme.palette.text.secondary }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 4,
              p: 2,
              backgroundColor: theme.palette.background.default,
              borderRadius: 2,
            }}
          >
            <Avatar
              alt={specialistName}
              src={avatarSrc}
              sx={{
                width: 45,
                height: 45,
                mr: 2,
                border: `2px solid ${theme.palette.primary.main}`,
              }}
            />
            <Box>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: theme.palette.text.primary }}
              >
                {specialistName}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary }}
              >
                {specialistTitle}
              </Typography>
            </Box>
          </Box>

          <List sx={{ mb: 3 }}>
            {navItems.map((item) => (
              <ListItem
                button
                key={item}
                onClick={handleDrawerToggle}
                component="a"
                href={`#${item.toLowerCase()}`}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  mb: 1,
                  "&:hover": {
                    backgroundColor: theme.palette.background.default,
                  },
                }}
              >
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{
                    fontWeight: 500,
                  }}
                />
              </ListItem>
            ))}
          </List>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            href="#formulario"
            startIcon={<LocalPhoneIcon />}
            sx={{
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
            }}
          >
            Falar agora
          </Button>
        </Drawer>
      </AppBar>
    </HideOnScroll>
  );
};
